import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import Coin from './kivo/Coin';

/**
 * Live credit-balance badge shown in the top bar (replacing the old logout
 * button). Tapping it opens the wallet / credit top-up page.
 *
 * The balance is sourced from {@link useUser} which polls the API on an
 * interval and on window focus, so the number is never stale. When the balance
 * runs low it switches to the coral "running low" treatment from the Kivo
 * design system.
 */
export default function CreditBadge({
  className,
  to = '/wallet',
}: {
  className?: string;
  to?: string;
}): React.JSX.Element | null {
  const { credits, authed } = useUser();
  const nav = useNavigate();

  if (!authed) return null;

  const total = credits?.total ?? 0;
  const low = total < 30;

  const palette = low
    ? {
        fg: 'var(--c-error)',
        bg: 'rgba(196,103,58,0.12)',
        line: 'rgba(196,103,58,0.30)',
      }
    : {
        fg: 'var(--c-deep-teal, #1A6F70)',
        bg: 'var(--c-teal-weak, rgba(42,164,166,0.12))',
        line: 'rgba(42,164,166,0.28)',
      };

  return (
    <button
      type="button"
      onClick={() => { nav(to); }}
      className={className}
      aria-label={`${total} credits — tap to top up`}
      data-testid="credit-badge"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        padding: '7px 12px 7px 10px',
        borderRadius: 999,
        background: palette.bg,
        border: `1px solid ${palette.line}`,
        color: palette.fg,
        font: '600 12.5px/1 var(--f-text)',
        cursor: 'pointer',
      }}
    >
      <Coin size={15} color={palette.fg} />
      {credits == null ? '—' : total.toLocaleString()}
    </button>
  );
}
